import { Link } from 'react-router-dom';
import { ShieldCheck, Truck, MessageCircle, Award, Sparkles, Leaf, Wind, Heart, ChevronRight } from 'lucide-react';
import ProductGrid from '../components/ProductGrid';
import BundleBuilder from '../components/BundleBuilder';
import Reviews from '../components/Reviews';
import Newsletter from '../components/Newsletter';
import TrustBanner from '../components/TrustBanner';
import UGCSection from '../components/UGCSection';
import ProductVideoShowcase from '../components/ProductVideoShowcase';
import FallingPetals from '../components/FallingPetals';
import { useProducts } from '../context/ProductsContext';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_home-rituals/artifacts/3k4kzjyy_image-1782216729254.png';
const SENS_IMG = 'https://images.pexels.com/photos/7546719/pexels-photo-7546719.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200';
const FLOOR_IMG = 'https://images.pexels.com/photos/13369847/pexels-photo-13369847.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200';
const KITCHEN_IMG = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80';
const BUNDLE_IMG = 'https://images.pexels.com/photos/7691101/pexels-photo-7691101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200';

export default function Home() {
  const { products, byCategory, fragrances } = useProducts();
  const bestsellers = products.filter(p => p.category !== 'bundle').slice(0, 4);
  const professional = byCategory('professional');
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="hero-sway w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/55 to-transparent" />
          <FallingPetals />
        </div>
        <div className="relative container-essenya py-28 md:py-40 max-w-3xl">
          <div className="text-eyebrow mb-5 animate-fade-up">Made in Italy · Home Care Premium</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-medium mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>Pulisci la casa.<br/>Lascia un profumo che resta.</h1>
          <p className="text-lg md:text-xl text-essenya-ink/80 max-w-xl mb-10 animate-fade-up" style={{ animationDelay: '0.2s' }}>Detergenti profumati Made in Italy, formulati per la pulizia quotidiana della casa e idonei anche ad ambienti soggetti a protocollo HACCP.</p>
          <div className="flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <Link to="/shop" data-testid="hero-cta-primary" className="btn-primary">Scopri i prodotti</Link>
            <Link to="/bundle" data-testid="hero-cta-secondary" className="btn-secondary">Crea il tuo bundle</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-2xl">
            {[
              { I: ShieldCheck, t: 'Pagamenti sicuri' },
              { I: Truck, t: 'Spedizione tracciata' },
              { I: MessageCircle, t: 'Assistenza WhatsApp' },
              { I: Award, t: 'Made in Italy' },
            ].map(({ I, t }, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <I size={18} strokeWidth={1.5} /><span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <TrustBanner />

      {/* Product Video Showcase — Kit 20,99€ */}
      <ProductVideoShowcase />

      {/* Micro benefits strip */}
      <section className="border-y border-essenya-border bg-white">
        <div className="relative">
          <div className="container-essenya py-5 flex gap-8 overflow-x-auto hide-scrollbar scroll-fade-x text-sm text-essenya-text scroll-smooth" data-testid="micro-benefits">
            {['Profumazioni intense', 'Formula concentrata', 'Ideali per uso quotidiano', 'Casa pulita e profumata', 'Packaging elegante', 'Made in Italy'].map((b, i) => (
              <span key={i} className="whitespace-nowrap flex items-center gap-2"><Sparkles size={14} strokeWidth={1.5} className="text-essenya-lilac" />{b}</span>
            ))}
          </div>
          <div className="md:hidden pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 flex items-center text-essenya-ink/50 animate-pulse">
            <ChevronRight size={18} strokeWidth={2} />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section data-testid="categories-section" className="section-padding">
        <div className="container-essenya">
          <div className="max-w-2xl mb-12">
            <div className="text-eyebrow mb-3">Categorie</div>
            <h2 className="text-4xl md:text-5xl font-display font-medium">Ogni ambiente ha il suo rituale.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { img: FLOOR_IMG, eyebrow: 'Lavapavimenti', title: 'Lavapavimenti profumati', desc: 'Per pavimenti puliti, brillanti e una profumazione elegante che si diffonde in tutta la casa.', cta: 'Scopri i lavapavimenti', link: '/lavapavimenti' },
              { img: KITCHEN_IMG, eyebrow: 'Sgrassatori', title: 'Sgrassatori profumati', desc: 'Per cucina, superfici e sporco quotidiano. Efficaci, pratici e piacevoli da usare.', cta: 'Scopri gli sgrassatori', link: '/sgrassatori' },
              { img: BUNDLE_IMG, eyebrow: 'Bundle', title: 'Bundle casa completa', desc: 'Abbina pavimenti e superfici in un unico kit per una casa sempre fresca, pulita e profumata.', cta: 'Crea il tuo kit', link: '/bundle' },
            ].map((c, i) => (
              <Link key={i} to={c.link} data-testid={`category-card-${i}`} className="group relative aspect-[4/5] rounded-2xl overflow-hidden block">
                <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
                  <div className="text-eyebrow text-white/80 mb-2">{c.eyebrow}</div>
                  <h3 className="font-display text-2xl md:text-3xl mb-2">{c.title}</h3>
                  <p className="text-white/85 text-sm mb-4 max-w-xs">{c.desc}</p>
                  <span className="text-sm font-medium underline underline-offset-4">{c.cta}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      <section data-testid="bestsellers-section" className="section-padding bg-white">
        <div className="container-essenya">
          <div className="max-w-2xl mb-12">
            <div className="text-eyebrow mb-3">Bestseller</div>
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-3">I più amati per una casa che profuma di pulito.</h2>
            <p className="text-essenya-text text-lg">Prodotti pensati per rendere ogni ambiente più fresco, ordinato e piacevole da vivere.</p>
          </div>
          <ProductGrid products={bestsellers} />
        </div>
      </section>

      {/* Essenya Professional */}
      <section data-testid="professional-section" className="section-padding">
        <div className="container-essenya">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-essenya-ink text-white p-8 md:p-16">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-essenya-champagne/10 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-eyebrow text-white/60 mb-4">Essenya Professional</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium mb-4 leading-tight">Soluzioni professionali per chi lavora con la pulizia.</h2>
                <p className="text-white/80 text-lg mb-8 max-w-lg">Detergenti ad alte prestazioni a base vegetale per ristorazione, GDO e industria alimentare. Efficacia certificata HACCP, formule concentrate e rispetto totale delle superfici.</p>
                <Link to="/shop?category=professional" data-testid="professional-cta" className="inline-block bg-white text-essenya-ink rounded-full px-7 py-3.5 text-sm font-medium hover:bg-essenya-champagne transition-colors">Scopri la linea Professional</Link>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {professional.map(p => (
                  <Link key={p.id} to={`/prodotto/${p.slug}`} data-testid={`pro-thumb-${p.id}`} className="bg-white rounded-2xl aspect-[3/4] overflow-hidden block hover:scale-105 transition-transform duration-300">
                    <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Builder */}
      <BundleBuilder />

      {/* How it works */}
      <section className="section-padding">
        <div className="container-essenya">
          <div className="max-w-2xl mb-12">
            <div className="text-eyebrow mb-3">Come funziona</div>
            <h2 className="text-4xl md:text-5xl font-display font-medium">Un gesto semplice. Una casa che cambia profumo.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: '01', t: 'Scegli il prodotto', d: 'Lavapavimenti per gli ambienti, sgrassatore per cucina e superfici.' },
              { n: '02', t: 'Usalo nella routine quotidiana', d: 'Basta poco prodotto per pulire e profumare in modo piacevole.' },
              { n: '03', t: 'Vivi la sensazione di casa pulita', d: 'Ambienti freschi, ordinati e profumati più a lungo.' },
            ].map((s, i) => (
              <div key={i} className="border-t border-essenya-ink pt-6">
                <span className="text-eyebrow">{s.n}</span>
                <h3 className="font-display text-2xl mt-3 mb-2">{s.t}</h3>
                <p className="text-essenya-text">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sensorial split */}
      <section data-testid="sensorial-section" className="bg-essenya-surface2">
        <div className="grid md:grid-cols-2">
          <div className="aspect-[4/5] md:aspect-auto md:min-h-[600px]">
            <img src={SENS_IMG} alt="Casa elegante" className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center px-8 md:px-16 py-16 md:py-24">
            <div className="max-w-md">
              <div className="text-eyebrow mb-3">Sensoriale</div>
              <h2 className="text-4xl md:text-5xl font-display font-medium mb-5">Non è solo pulizia.</h2>
              <p className="text-essenya-text text-lg mb-8">È la sensazione di rientrare in casa e percepire subito ordine, freschezza e cura. ESSENYA trasforma i gesti quotidiani in piccoli rituali di benessere domestico.</p>
              <Link to="/shop" className="btn-primary" data-testid="sensorial-cta">Trova la tua profumazione</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Before / After */}
      <section data-testid="before-after-section" className="section-padding">
        <div className="container-essenya">
          <div className="max-w-2xl mb-12">
            <div className="text-eyebrow mb-3">Prima / Dopo</div>
            <h2 className="text-4xl md:text-5xl font-display font-medium">Dal pulito funzionale al pulito sensoriale.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-essenya-surface2 rounded-2xl p-10">
              <div className="text-eyebrow mb-4 text-essenya-text">Prima</div>
              <p className="font-display text-2xl leading-snug">Detergenti anonimi, profumi aggressivi, confezioni da nascondere.</p>
            </div>
            <div className="bg-essenya-ink text-white rounded-2xl p-10">
              <div className="text-eyebrow mb-4 text-white/60">Dopo</div>
              <p className="font-display text-2xl leading-snug">Prodotti efficaci, fragranze curate, packaging elegante e una casa che profuma davvero di pulito.</p>
              <Link to="/shop" className="mt-8 inline-block underline underline-offset-4">Scopri la differenza ESSENYA →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fragrances */}
      <section data-testid="fragrances-section" className="section-padding bg-white">
        <div className="container-essenya">
          <div className="max-w-2xl mb-12">
            <div className="text-eyebrow mb-3">Fragranze</div>
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-3">Scegli il profumo della tua casa.</h2>
            <p className="text-essenya-text text-lg">Ogni fragranza racconta un modo diverso di vivere gli ambienti.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {fragrances.map(f => (
              <Link key={f.slug} to={`/shop?fragrance=${f.slug}`} data-testid={`fragrance-${f.slug}`} className="group relative aspect-[4/5] rounded-xl overflow-hidden block">
                <img src={f.image} alt={f.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                  <p className="font-display text-xl">{f.name}</p>
                  <p className="text-white/80 text-xs">{f.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section data-testid="trust-section" className="section-padding bg-essenya-bg">
        <div className="container-essenya">
          <div className="max-w-2xl mb-12">
            <div className="text-eyebrow mb-3">Fiducia</div>
            <h2 className="text-4xl md:text-5xl font-display font-medium">Acquista con tranquillità.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { I: ShieldCheck, t: 'Pagamenti protetti', d: 'Carta, PayPal, Apple Pay e Google Pay' },
              { I: Truck, t: 'Spedizione tracciata', d: 'Consegna in 2-3 giorni' },
              { I: MessageCircle, t: 'Assistenza WhatsApp', d: 'Risposte rapide e dirette' },
              { I: Heart, t: 'Resi chiari', d: 'Cambia idea entro 14 giorni' },
            ].map(({ I, t, d }, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-essenya-border">
                <I size={22} strokeWidth={1.5} className="mb-4" />
                <p className="font-medium mb-1">{t}</p>
                <p className="text-essenya-text text-sm">{d}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-essenya-text mt-8 text-sm">Ogni ordine viene gestito con cura, dalla conferma all'arrivo a casa.</p>
        </div>
      </section>

      <Reviews />
      <UGCSection />
      <Newsletter />
    </main>
  );
}
